/* eslint-disable @nx/enforce-module-boundaries */
import { VStack } from '@chakra-ui/react';
import {
  Brifecase,
  Currency,
  Industry,
  SaveJobs,
  LocationIcon,
  CompanyIndustryIcon,
  JobPlaceholderIcon,
} from '@mybridge/icons';
import { SavedJobIcon } from '@mybridge/icons/SavedJob';
import {
  Avatar,
  Box,
  Button,
  HStack,
  Heading,
  IconButton,
  Link,
  Stack,
  Text,
} from '@mybridge/ui';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { useRouter } from 'next/router';
import { forwardRef, useState, useEffect, useContext } from 'react';
import { JobsListingContext } from 'v4/snippets/jobs/context';
import { ForceSignInWrapper } from 'v4/components/force-sign-in-wrapper';
import {
  convertHTMLToPlainText,
  currencyFormatter,
  formatLocation,
} from 'v4/lib/commons';
import { EMPLOYMENT_CHOICES, WORKPLACE_TYPE } from 'v4/lib/data';

export const SearchJobCard = forwardRef(
  (
    {
      isApplied = false,
      appliedData = null,
      appliedDate = null,
      onApplyClick,
      job,
      onViewDetailsClick,
      onSaveClick,
      saveLoading,
      isSaved = false,
      href,
      ...props
    },
    ref
  ) => {
    const {
      hiring_company_details: company,
      currency_choice: currency,
      compensation_range_min_amt: minSalary,
      compensation_range_max_amt: maxSalary,
      benefits,
      employment_type: empType,
      job_description: description,
      payment_choice,
      remote,
      how_to_apply,
      how_to_apply_text,
    } = job ?? {};
    const { t } = useTranslator();
    const { appliedJobsListing } = useContext(JobsListingContext);
    const applied = appliedData ?? appliedJobsListing?.find(
      (aj) => aj?.job_details?.id === job?.id || aj?.id === job?.id || aj?.job?.id === job?.id
    );

    const formatDate = (dateString) => {
      if (!dateString) return '';
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('en-US', options);
    };

    const appliedDateVal = appliedDate || applied?.applied_date || applied?.created_at || job?.applied_date;
    const jobLocation = formatLocation(job);
    const descPlainText = convertHTMLToPlainText(description ?? '');
    const { push } = useRouter();
    const [imageError, setImageError] = useState(false);
    
    // Reset image error when image source changes
    useEffect(() => {
      setImageError(false);
    }, [company?.image]);

    const jsonString = job?.how_to_apply_text
      ?.replace(/'/g, '"')
      ?.replace(/^\[|\]$/g, '')
      .split(',')[0] // Split by comma and take the first element
      .trim(); // Change single quotes to double and remove brackets

    return (
      <Stack h="100%" {...props}>
        <Stack>
          <HStack pt={1} alignItems="center">
            {/* <HStack flex={1}> */}
            {!company?.image || imageError ? (
              <Stack
                style={{ backgroundColor: '#D9D9D9' }}
                textAlign="center"
                alignItems="center"
                justifyContent="center"
                width="80px"
                height="80px"
                borderRadius="50%"
                overflow="hidden"
              >
                <JobPlaceholderIcon width="65%" height="65%" color="#9F9F9F" />
              </Stack>
            ) : (
              <Avatar
                border="1px solid"
                borderColor="blackAlpha.100"
                src={company?.image}
                w="80px"
                h="80px"
                borderRadius="50%"
                alt={company?.name}
                onError={() => setImageError(true)}
              />
            )}
            <Heading
              {...(href ? { as: Link, href } : {})}
              color="#2E2E2A"
              fontSize="24px"
              fontWeight="500"
              ml={2}
            >
              {job?.title}

              <Text
                fontWeight="medium"
                fontSize="sm"
                lineHeight={'22px'}
                color="#5B5B5B"
              >
                {job?.days_published > 0 ? job?.days_published : ''}
                {job?.days_published > 0 && ` ${t('jobs.daysAgo')}`}
              </Text>
            </Heading>
            {/* </HStack> */}
            <Stack></Stack>
          </HStack>
        </Stack>

        <Stack h="100%" spacing={0}>
          <Stack spacing={0}>
            <HStack pt={1} alignItems="center">
              <CompanyIndustryIcon color="#9F9F9F" />
              <Text display="inline" fontSize="sm">
                {company?.name}
              </Text>
            </HStack>
            {jobLocation && (
              <HStack pt={1} alignItems="center">
                <LocationIcon color="#9F9F9F" />
                <Text display="inline" fontSize="sm" ml="1%">
                  {jobLocation}
                </Text>
              </HStack>
            )}
            {minSalary > 0 && (
              <HStack pt={1} alignItems="center">
                <Box pt={'2px'}>
                  <Currency color="#9F9F9F" />
                </Box>
                {maxSalary ? (
                  <Text fontSize="sm">
                    {currencyFormatter(minSalary, currency)} to{' '}
                    {currencyFormatter(maxSalary, currency)}{' '}
                    {payment_choice
                      .toLowerCase()
                      .replace('ANNUALLY', 'Annually')}
                  </Text>
                ) : (
                  <Text fontSize="sm">
                    {currencyFormatter(minSalary, currency)}{' '}
                    {payment_choice
                      .toLowerCase()
                      .replace('ANNUALLY', 'Annually')}
                  </Text>
                )}
              </HStack>
            )}
            {benefits?.length > 0 && (
              <HStack pt={1} alignItems="center">
                <Box pt={'2px'}>
                  <Industry color="#9F9F9F" />
                </Box>
                <Text fontSize="sm">
                  {benefits?.map?.((b) => b?.name)?.join(', ')}
                </Text>
              </HStack>
            )}
            <HStack pt={1} alignItems="center">
              <Box pt={'2px'}>
                <Brifecase color="#9F9F9F" />
              </Box>
              <Text fontSize="sm">
                {EMPLOYMENT_CHOICES?.find((ec) => ec.value === empType)?.label}{' '}
                | {WORKPLACE_TYPE?.find((ec) => ec.value === remote)?.label}
              </Text>
            </HStack>
          </Stack>

          <HStack
            h="100%"
            alignItems="flex-end"
            justifyContent="flex-start"
            pt={4}
          >
            {/* <ForceSignInWrapper> */}
              <HStack spacing={2}>
                {isApplied ? (
                  <Button
                    fontWeight="semibold"
                    border="1px solid #38A169"
                    borderRadius="50px"
                    bg="none"
                    fontSize="14px"
                    color="green.500"
                  >
                    {t('jobs.applied') || "Applied"}{appliedDateVal ? ` | ${formatDate(appliedDateVal)}` : ''}
                  </Button>
                ) : how_to_apply === 'EXTERNAL' ? (
                  <Button
                    onClick={() => {
                      const cleanedUrl = jsonString.replace(/['"]/g, '').trim();
                      const jsonStrings =
                        cleanedUrl.startsWith('https://') ||
                        cleanedUrl.startsWith('http://')
                          ? cleanedUrl
                          : `https://${cleanedUrl.replace(/^https?:\/\//, '')}`;

                      if (
                        cleanedUrl &&
                        cleanedUrl !== 'https://' &&
                        cleanedUrl !== 'http://'
                      ) {
                        window?.open(jsonStrings, '_blank');
                      } else {
                        console.error('Invalid URL:', jsonStrings);
                      }
                    }}
                    target="_blank"
                    border="1px solid gray"
                    borderRadius="50px"
                    bg="none"
                    color="#5B5B5B"
                    fontSize="14px"
                  >
                    {t('pageJobs.apply') || "Apply"}
                  </Button>
                ) : (
                  <ForceSignInWrapper>
                    <Button
                      onClick={onApplyClick}
                      border="1px solid gray"
                      borderRadius="50px"
                      bg="none"
                      color="#5B5B5B"
                      fontSize="14px"
                    >
                      {t('pageJobs.apply') || "Apply"}
                    </Button>
                  </ForceSignInWrapper>
                )}
                <Button
                  onClick={() => {push(
                    `/jobs/${job?.id}`,
                  )}}
                  // onClick={onViewDetailsClick}
                  border="1px solid gray"
                  borderRadius="50px"
                  bg="none"
                  color="#5B5B5B"
                  fontSize="14px"
                >
                  {t('pageJobs.viewDetails') || "View Details"}
                </Button>
                <ForceSignInWrapper>
                  <IconButton
                    aria-label={isSaved ? 'Unsave job' : 'Save job'}
                    isLoading={saveLoading}
                    onClick={onSaveClick}
                    border="1px solid gray"
                    borderRadius="50px"
                    bg="none"
                    color="#5B5B5B"
                    fontSize="14px"
                  >
                    {isSaved ? (
                      <SavedJobIcon width="20" height="20" />
                    ) : (
                      <SaveJobs width="20" height="20" />
                    )}
                  </IconButton>
                </ForceSignInWrapper>
              </HStack>
            {/* </ForceSignInWrapper> */}
          </HStack>
        </Stack>
      </Stack>
    );
  }
);
